import CompanyTypePage from './CompanyTypePage';
import { useState, useEffect } from 'react';
import { companiesAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';

const CONFIG = {
  icon: '👤',
  name: 'One Person Company (OPC)',
  shortName: 'OPC / Startup',
  subtitle: 'Single-member company structure for solo entrepreneurs · Sec 2(62) · Companies Act 2013',
  tags: ['Single Member', 'Limited Liability', 'Nominee Director Required', 'Min Capital: None', 'Simplified Compliance'],
  heroBg: 'linear-gradient(135deg, #f7f2e2, #efe4c6)',
  heroAccent: '#d9c283',
  titleColor: '#6b5418',
  subtitleColor: '#836016',
  tagBg: '#f3ebd7',
  tagText: '#6b5418',
  accentColor: '#8a6a1f',
  // Stats will be dynamically loaded
  stats: [],
  filings: [
    { form: 'AOC-4',     title: 'Financial Statements',  deadline: '180d from FY end',  penalty: '₹100/day',    status: 'filed' },
    { form: 'MGT-7A',    title: 'Annual Return (OPC)',   deadline: '60d from AGM',       penalty: '₹100/day',    status: 'pending' },
    { form: 'ADT-1',     title: 'Auditor Appointment',   deadline: '15d from AGM',       penalty: '1×–12× fee by delay',    status: 'filed' },
    { form: 'DIR-3 KYC', title: 'Director KYC',          deadline: '30 Jun / 3 yrs',     penalty: 'DIN blocked', status: 'filed' },
  ],
  requirements: [
    { icon: '👤', title: 'Single Member',       desc: 'Only one natural person (Indian citizen/resident) can be member — must nominate a successor' },
    { icon: '🔄', title: 'Mandatory Conversion',desc: 'Must convert to Pvt Ltd if paid-up capital > ₹50 lakh or turnover > ₹2 crore' },
    { icon: '📋', title: 'Board Meetings',      desc: 'Minimum 1 board meeting per half-year (2 per year) — simpler than Pvt Ltd requirement' },
    { icon: '🏛️', title: 'AGM',               desc: 'No AGM required for OPC — member resolution by written form suffices' },
    { icon: '📄', title: 'Nominee Requirement', desc: 'Mandatory to appoint a nominee who takes over on death/incapacity of sole member' },
    { icon: '💼', title: 'Audit',              desc: 'Statutory audit mandatory regardless of turnover — one-person does not exempt from audit' },
  ],
  complianceHealth: [
    { label: 'AOC-4 Filed',      percent: 80, color: '#4a6b3f' },
    { label: 'MGT-7A Filed',     percent: 70, color: '#8a6a1f' },
    { label: 'DIR-3 KYC Done',   percent: 92, color: '#4a6b3f' },
  ],
};

const CACHE_KEY = 'companyType:opc';

export default function OPCPage() {
  // Stale-while-revalidate: seed the last real config so the stats strip does
  // not reset to empty on revisit (services/viewCache).
  const [dynamicConfig, setDynamicConfig] = useState(() => getView(CACHE_KEY) ?? CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await companiesAPI.getOPCStats();
        if (response.success && response.data) {
          const stats = response.data;
          const dynamicStats = [
            { label: 'OPCs in Portfolio',   value: stats.totalCompanies.toString(), color: 'text-[#3a4a66]' },
            { label: 'Fully Compliant',     value: stats.fullyCompliant.toString(), color: 'text-[#4a6b3f]' },
            { label: 'Overdue',             value: stats.overdueFilings.toString(), color: 'text-[#9a564a]' },
            { label: 'Pending Actions',     value: stats.pendingActions.toString(), color: 'text-[#8a6a1f]' },
          ];
          
          const nextConfig = { ...CONFIG, stats: dynamicStats };
          setDynamicConfig(nextConfig);
          setView(CACHE_KEY, nextConfig);
        }
      } catch (error) {
        console.error('Failed to fetch OPC stats:', error);
        // Keep default stats if API fails
        const defaultStats = [
          { label: 'OPCs in Portfolio',   value: '0', color: 'text-[#3a4a66]' },
          { label: 'Fully Compliant',     value: '0', color: 'text-[#4a6b3f]' },
          { label: 'Overdue',             value: '0', color: 'text-[#9a564a]' },
          { label: 'Pending Actions',     value: '0', color: 'text-[#8a6a1f]' },
        ];
        if (!hasView(CACHE_KEY)) {
          setDynamicConfig(prev => ({
            ...prev,
            stats: defaultStats
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <CompanyTypePage config={dynamicConfig} />;
  }

  return <CompanyTypePage config={dynamicConfig} />;
}
